import { html, LitElement, PropertyValues, TemplateResult } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { WEB_COMPONENT_RESTART_ENDPOINT } from '../../../../../webcomponents.constants';
import { RestartEndpointTaskSpecifics, RestartType } from '../types';
import { translateFn } from '../../../../../utils/translator.helper';
import { styles } from './view/styles';
import { GzDatePickerWebComponent } from '../../../../../ui/gzDatePicker';
import { PickerStrings } from '../../../../../ui/gzDatePicker/types';
import { GzRadioGroupWebComponent } from '../../../../../ui/gzRadio';
import { GzFormFieldWebComponent } from '../../../../../ui/gzForm/formField';
import { CustomizableValidator } from '../../../../../ui/gzForm/validators/customizable.validator';
import { ValidationResult } from '../../../../../ui/gzForm/formField/types/form-validator';
import { NetworkPopUpEvents } from '../../NetworkBasePopUp';
import { FontSizes } from '../../../../../types/common/types';
import { computeMinTimestampForDatePicker } from '../../../helpers/datePicker.utils';
import { GzDropdownMenuWebComponent } from '../../../../../ui/gzDropdownMenu';

@customElement(WEB_COMPONENT_RESTART_ENDPOINT)
export class GzRestartEndpointWebComponent extends LitElement {
  @query('#custom-datetime-picker')
  private dateTimePicker: GzDatePickerWebComponent;

  @query('#restart-type-group')
  private restartTypeGroup: GzRadioGroupWebComponent;

  @query('#radio-group-form-field')
  private formField: GzFormFieldWebComponent<GzRadioGroupWebComponent>;

  private pickerStrings: PickerStrings = {
    currentDay: translateFn('This month'),
    confirmActionButton: translateFn('Apply'),
    cancelActionButton: translateFn('Cancel'),
  };

  protected async firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    await this.updateComplete;
    this.formField.Validators = [CustomizableValidator(this.validateGeneralForm.bind(this))];
    this.formField.valid$.subscribe(valid => {
      this.dispatchEvent(
        new CustomEvent(NetworkPopUpEvents.SET_CONFIRM_BUTTON, {
          detail: { value: !valid },
          bubbles: true,
          composed: true,
        })
      );
    });

    const dropdownMenu = this.dateTimePicker.shadowRoot?.querySelector(
      'gz-dropdown-menu'
    ) as GzDropdownMenuWebComponent;
    dropdownMenu.addEventListener('close', this.handleDateSelected.bind(this));
  }

  static get styles() {
    return styles;
  }

  private renderDescription(): TemplateResult {
    return html`
      <gz-text fontSize="${FontSizes.HEADING_6}">
        ${translateFn('Select the time for the target endpoints to initiate a restart:')}
      </gz-text>
    `;
  }

  public getFormValue(): RestartEndpointTaskSpecifics {
    const restartType = this.restartTypeGroup.getFormValue() as RestartType;
    const specifics: RestartEndpointTaskSpecifics = {
      restartTime: restartType,
    };
    if (restartType === RestartType.NOW) {
      return specifics;
    }

    return {
      ...specifics,
      ...this.transformDate(this.dateTimePicker.selectedTimestamp * 1000),
    };
  }

  protected render(): unknown {
    return html`
      <div class="pl-5 pt-5">
        ${this.renderDescription()}
        <gz-form-field id="radio-group-form-field">
          <gz-radio-group
            class="pt-1"
            gz-form-input
            id="restart-type-group"
            groupName="restart-group"
            .selectedOption="${RestartType.NOW}"
            @radioSelectionChanged="${this.handleTypeChanged}"
          >
            <gz-radio
              class="pt-1"
              name="restart-group"
              .value="${RestartType.NOW}"
              label="${translateFn('Restart now')}"
            >
            </gz-radio>
            <div class="d-flex">
              <gz-radio
                class="pt-1 pr-5"
                name="restart-group"
                .value="${RestartType.CUSTOM}"
                label="${translateFn('Restart on:')}"
              >
              </gz-radio>
              <gz-date-picker
                id="custom-datetime-picker"
                disabled
                placeholder="${translateFn('Time and date')}"
                userLocale="${Application.Main.getUser().profile.language}"
                timezone="${Application.Main.getUser().profile.timezone}"
                min="${computeMinTimestampForDatePicker()}"
                hasTimePicker
                .pickerStrings="${this.pickerStrings}"
                @dateIsSelected="${this.handleDateSelected}"
                @close="${this.handleDateSelected}"
              >
              </gz-date-picker>
            </div>
          </gz-radio-group>
          <gz-input-error></gz-input-error>
        </gz-form-field>
      </div>
    `;
  }

  private handleTypeChanged(event: CustomEvent) {
    const selectedValue = event.detail.selectedValue as RestartType;
    this.dateTimePicker.disabled = selectedValue === RestartType.NOW;
  }

  private transformDate(dateTimestampInMs: number): Partial<RestartEndpointTaskSpecifics> {
    const dateObj = new Date(dateTimestampInMs);

    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const year = String(dateObj.getFullYear()).slice(-2);
    const hour = dateObj.getHours();
    const minute = dateObj.getMinutes();

    return {
      restartOnDate: `${month}/${day}/${year}`,
      restartOnHour: hour,
      restartOnMinute: minute,
    };
  }

  private validateGeneralForm(value): ValidationResult {
    if (value === RestartType.NOW) {
      return {
        valid: true,
      };
    }

    const selectedTimestamp = this.dateTimePicker.selectedTimestamp;
    if (selectedTimestamp === undefined) {
      return {
        valid: false,
        error: translateFn('Select a valid time and date.'),
      };
    }

    const currentTime = Math.floor(Date.now() / 1000);
    if (currentTime - selectedTimestamp >= 60) {
      return {
        valid: false,
        error: translateFn('Select a valid time and date.'),
      };
    }

    return {
      valid: true,
    };
  }

  private handleDateSelected() {
    this.formField.validate(true);
  }
}
